import type { BaseContactDetail, CountryCodeMapping } from '@/api';
import { isEmailValid, validateIntlPhone } from './api';
import type { ValidationResult } from './index.types';

export const isValidIndianNumber = (mobile: string): boolean => {
	const mobileRegex = /^[6-9]\d{9}$/;
	return mobileRegex.test(mobile) || false;
};

/**
 * Validates a phone number based on country code
 *
 * @param phoneNumber - The phone number to validate (without country code)
 * @param isdInfo - Country code information object containing code, number and name
 * @returns Promise resolving to a boolean indicating if the phone number is valid
 */
export const validatePhoneNumber = async (
	phoneNumber: string,
	isdInfo: CountryCodeMapping,
): Promise<boolean> => {
	try {
		// For Indian numbers
		if (isdInfo.number === '91') {
			return isValidIndianNumber(phoneNumber);
		}

		// For international numbers
		const validateResponse = await validateIntlPhone({
			mobile: parseInt(phoneNumber, 10),
			countryCode: isdInfo.code,
		});

		return validateResponse?.isValid || false;
	} catch {
		return false;
	}
};

/**
 * Finds the country code information based on ISD number
 * @param isdCode - The ISD number to search for (e.g., "IN" for India)
 * @param countryCodeMapping - Array of country code mapping objects
 * @returns The matching country code information or undefined if not found
 */
export const getIsdInfo = (
	isdCode: string,
	countryCodeMapping: CountryCodeMapping[],
) => {
	return countryCodeMapping.find((item) => item.code === isdCode);
};

export const validateFullName = (rawFullName: string): ValidationResult => {
	const fullName = (rawFullName ?? '').trim();

	if (!fullName.length) {
		return { valid: false, error: 'Full name is required' };
	}

	if (fullName.length > 100) {
		return { valid: false, error: 'Full name cannot exceed 100 characters' };
	}

	// Only allow alphabets (upper/lower case), spaces, and apostrophes
	const fullNameRegex = /^[a-zA-Z\s']+$/;
	if (!fullNameRegex.test(fullName)) {
		return {
			valid: false,
			error: 'Full name can only contain alphabets, spaces, and apostrophes',
		};
	}

	return { valid: true, error: null };
};

export const validateEmail = async (
	rawEmail: string,
): Promise<ValidationResult> => {
	const email = (rawEmail ?? '').trim();

	if (!email.length) {
		return { valid: false, error: 'Please enter a valid email address' };
	}

	try {
		const valid = await isEmailValid(email);
		if (!valid) {
			return { valid: false, error: 'Please enter a valid email address' };
		}
		return { valid: true, error: null };
	} catch {
		return { valid: false, error: 'Error validating email' };
	}
};

export const validateContactDetail = async (
	contactDetails: BaseContactDetail,
): Promise<ValidationResult> => {
	const { userName, email, mobileInfo } = contactDetails;
	const { number: mobileNumber, isdInfo } = mobileInfo;
	const userNameResult = validateFullName(userName);
	if (!userNameResult.valid) {
		return { valid: false, error: userNameResult.error };
	}
	const emailResult = await validateEmail(email);
	if (!emailResult.valid) {
		return { valid: false, error: emailResult.error };
	}
	const phoneNumberResult = await validatePhoneNumber(mobileNumber, isdInfo);
	if (!phoneNumberResult) {
		return { valid: false, error: 'Please enter a valid phone number' };
	}
	return { valid: true, error: null };
};
